/**
 * Branding Service
 *
 * Manages tenant/organization branding including colors, logos, and custom styling.
 * Applies branding dynamically via CSS custom properties.
 */

import type { Organization } from '../types';
import { eventBus, Events } from './events';

export interface BrandingConfig {
  primaryColor?: string;
  secondaryColor?: string;
  logoUrl?: string;
  faviconUrl?: string;
  headerBackground?: string;
  headerTextColor?: string;
  accentColor?: string;
  fontFamily?: string;
}

export interface TenantBranding extends BrandingConfig {
  organizationId: string;
  organizationName: string;
  organizationSlug: string;
}

// Default Moya branding
const DEFAULT_BRANDING: BrandingConfig = {
  primaryColor: '#2563eb',
  secondaryColor: '#3b82f6',
  accentColor: '#60a5fa',
  headerBackground: 'linear-gradient(180deg, rgba(30, 41, 59, 0.98) 0%, rgba(15, 23, 42, 0.98) 100%)',
  headerTextColor: '#ffffff',
  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
};

// CSS variable mappings
const CSS_VAR_MAP: Record<keyof BrandingConfig, string> = {
  primaryColor: '--moya-primary-color',
  secondaryColor: '--moya-secondary-color',
  accentColor: '--moya-accent-color',
  headerBackground: '--moya-header-bg',
  headerTextColor: '--moya-header-text',
  logoUrl: '--moya-logo-url',
  faviconUrl: '--moya-favicon-url',
  fontFamily: '--moya-font-family',
};

class BrandingService {
  private currentBranding: TenantBranding | null = null;
  private styleElement: HTMLStyleElement | null = null;

  constructor() {
    // Listen for organization switch events
    eventBus.on(Events.ORGANIZATION_SWITCHED, (data: unknown) => {
      const org = data as Organization;
      if (org?.id) {
        this.applyOrganizationBranding(org);
      }
    });
  }

  /**
   * Initialize branding from organization data
   */
  applyOrganizationBranding(org: Organization): void {
    const branding: TenantBranding = {
      organizationId: org.id,
      organizationName: org.name,
      organizationSlug: org.slug,
      primaryColor: org.primaryColor || DEFAULT_BRANDING.primaryColor,
      logoUrl: org.logoUrl,
    };

    // Generate complementary colors from primary
    if (org.primaryColor) {
      branding.secondaryColor = this.lightenColor(org.primaryColor, 20);
      branding.accentColor = this.lightenColor(org.primaryColor, 40);
      branding.headerBackground = `linear-gradient(180deg, ${this.darkenColor(org.primaryColor, 30)} 0%, ${this.darkenColor(org.primaryColor, 50)} 100%)`;
    }

    this.applyBranding(branding);
  }

  /**
   * Apply branding configuration
   */
  applyBranding(branding: Partial<TenantBranding>): void {
    this.currentBranding = {
      ...DEFAULT_BRANDING,
      ...this.currentBranding,
      ...branding,
    } as TenantBranding;

    this.applyCSS(this.currentBranding);
    this.updateFavicon(this.currentBranding.faviconUrl);
    this.updatePageTitle(this.currentBranding.organizationName);

    eventBus.emit(Events.BRANDING_CHANGED, this.currentBranding);
  }

  /**
   * Reset to default Moya branding
   */
  resetToDefault(): void {
    this.currentBranding = null;
    this.applyCSS(DEFAULT_BRANDING);
    this.updateFavicon(undefined);
    this.updatePageTitle(undefined);
  }

  /**
   * Get current branding config
   */
  getBranding(): TenantBranding | BrandingConfig {
    return this.currentBranding || DEFAULT_BRANDING;
  }

  /**
   * Apply CSS custom properties to document
   */
  private applyCSS(config: BrandingConfig): void {
    // Create or get style element
    if (!this.styleElement) {
      this.styleElement = document.createElement('style');
      this.styleElement.id = 'moya-branding';
      document.head.appendChild(this.styleElement);
    }

    // Build CSS variables
    const cssVars: string[] = [];
    for (const [key, cssVar] of Object.entries(CSS_VAR_MAP)) {
      const value = config[key as keyof BrandingConfig];
      if (value && key !== 'logoUrl' && key !== 'faviconUrl') {
        cssVars.push(`${cssVar}: ${value};`);
      }
    }

    // Apply to root
    this.styleElement.textContent = `
      :root {
        ${cssVars.join('\n        ')}
      }

      /* Apply to Platform Shell components */
      moya-platform-header {
        --header-bg: var(--moya-header-bg);
        --header-border: rgba(255, 255, 255, 0.1);
      }
    `;
  }

  /**
   * Update page favicon
   */
  private updateFavicon(url?: string): void {
    let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');

    if (!url) {
      // Reset to default
      return;
    }

    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }

    link.href = url;
  }

  /**
   * Update page title with organization name
   */
  private updatePageTitle(orgName?: string): void {
    const baseTitle = document.title.replace(/^.*\s-\s/, '');
    if (orgName) {
      document.title = `${orgName} - ${baseTitle}`;
    }
  }

  /**
   * Color manipulation utilities
   */
  private hexToHSL(hex: string): { h: number; s: number; l: number } {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    if (!result) return { h: 0, s: 0, l: 0 };

    const r = parseInt(result[1], 16) / 255;
    const g = parseInt(result[2], 16) / 255;
    const b = parseInt(result[3], 16) / 255;

    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h = 0;
    let s = 0;
    const l = (max + min) / 2;

    if (max !== min) {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      switch (max) {
        case r:
          h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
          break;
        case g:
          h = ((b - r) / d + 2) / 6;
          break;
        case b:
          h = ((r - g) / d + 4) / 6;
          break;
      }
    }

    return { h: h * 360, s: s * 100, l: l * 100 };
  }

  private hslToHex(h: number, s: number, l: number): string {
    s /= 100;
    l /= 100;

    const a = s * Math.min(l, 1 - l);
    const f = (n: number) => {
      const k = (n + h / 30) % 12;
      const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
      return Math.round(255 * color)
        .toString(16)
        .padStart(2, '0');
    };

    return `#${f(0)}${f(8)}${f(4)}`;
  }

  private lightenColor(hex: string, amount: number): string {
    const hsl = this.hexToHSL(hex);
    hsl.l = Math.min(100, hsl.l + amount);
    return this.hslToHex(hsl.h, hsl.s, hsl.l);
  }

  private darkenColor(hex: string, amount: number): string {
    const hsl = this.hexToHSL(hex);
    hsl.l = Math.max(0, hsl.l - amount);
    return this.hslToHex(hsl.h, hsl.s, hsl.l);
  }
}

// Export singleton instance
export const brandingService = new BrandingService();
export default brandingService;
